import { useState } from 'react'
import type { TeamMember, Invitation } from '@/hooks/useTeam'
import { RoleBadge } from './RoleSelector'
import RoleSelector from './RoleSelector'

interface TeamMemberTableProps {
  members: TeamMember[]
  invitations: Invitation[]
  currentUserId: string
  onRemoveMember: (userId: string) => void
  onUpdateRole: (userId: string, role: string) => void
  onCancelInvitation: (invitationId: string) => void
  isUpdatingRole?: boolean
  isRemoving?: boolean
}

export default function TeamMemberTable({
  members,
  invitations,
  currentUserId,
  onRemoveMember,
  onUpdateRole,
  onCancelInvitation,
  isUpdatingRole = false,
  isRemoving = false,
}: TeamMemberTableProps) {
  const [editingRoleId, setEditingRoleId] = useState<string | null>(null)
  const [confirmRemoveId, setConfirmRemoveId] = useState<string | null>(null)

  const handleRoleChange = (userId: string, newRole: string) => {
    onUpdateRole(userId, newRole)
    setEditingRoleId(null)
  }

  const handleRemove = (userId: string) => {
    onRemoveMember(userId)
    setConfirmRemoveId(null)
  }

  // Filter out expired invitations
  const pendingInvitations = invitations.filter(
    (inv) => inv.status === 'pending' && new Date(inv.expires_at || '') > new Date()
  )

  const isEmpty = members.length === 0 && pendingInvitations.length === 0

  if (isEmpty) {
    return (
      <div className="card p-12 text-center">
        <div className="text-4xl mb-4">
          <svg className="w-12 h-12 mx-auto text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
          </svg>
        </div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">No team members yet</h3>
        <p className="text-gray-500">
          Invite colleagues to collaborate on your Personic account.
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Active Members */}
      <div className="card overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
          <h3 className="text-sm font-medium text-gray-900">
            Team Members ({members.length})
          </h3>
        </div>

        {/* Desktop Table */}
        <div className="hidden md:block">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Member
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Role
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Last Active
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {members.map((member) => {
                const isCurrentUser = member.id === currentUserId
                const isEditingRole = editingRoleId === member.id
                const isConfirmingRemove = confirmRemoveId === member.id

                return (
                  <tr key={member.id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="w-10 h-10 rounded-full bg-personic-100 flex items-center justify-center">
                          <span className="text-personic-600 font-medium">
                            {member.name?.charAt(0).toUpperCase() || '?'}
                          </span>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            {member.name}
                            {isCurrentUser && (
                              <span className="ml-2 text-xs text-gray-500">(you)</span>
                            )}
                          </div>
                          <div className="text-sm text-gray-500">{member.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {isEditingRole ? (
                        <div className="flex items-center gap-2">
                          <RoleSelector
                            value={member.role}
                            onChange={(role) => handleRoleChange(member.id, role)}
                            disabled={isUpdatingRole}
                            className="w-32"
                          />
                          <button
                            onClick={() => setEditingRoleId(null)}
                            className="text-gray-400 hover:text-gray-600"
                          >
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                      ) : (
                        <RoleBadge role={member.role} />
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`px-2 py-1 text-xs font-medium rounded-full ${
                          member.is_active
                            ? 'bg-green-100 text-green-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}
                      >
                        {member.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {member.last_login
                        ? new Date(member.last_login).toLocaleDateString()
                        : 'Never'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                      {!isCurrentUser && (
                        <div className="flex justify-end gap-2">
                          {!isEditingRole && !isConfirmingRemove && (
                            <>
                              <button
                                onClick={() => setEditingRoleId(member.id)}
                                className="text-personic-600 hover:text-personic-700"
                              >
                                Edit Role
                              </button>
                              <button
                                onClick={() => setConfirmRemoveId(member.id)}
                                className="text-red-600 hover:text-red-700"
                              >
                                Remove
                              </button>
                            </>
                          )}
                          {isConfirmingRemove && (
                            <div className="flex items-center gap-2">
                              <span className="text-gray-500">Remove?</span>
                              <button
                                onClick={() => handleRemove(member.id)}
                                disabled={isRemoving}
                                className="text-red-600 hover:text-red-700 font-medium"
                              >
                                Yes
                              </button>
                              <button
                                onClick={() => setConfirmRemoveId(null)}
                                className="text-gray-500 hover:text-gray-700"
                              >
                                No
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>

        {/* Mobile Cards */}
        <div className="md:hidden divide-y divide-gray-200">
          {members.map((member) => {
            const isCurrentUser = member.id === currentUserId

            return (
              <div key={member.id} className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex items-center">
                    <div className="w-10 h-10 rounded-full bg-personic-100 flex items-center justify-center">
                      <span className="text-personic-600 font-medium">
                        {member.name?.charAt(0).toUpperCase() || '?'}
                      </span>
                    </div>
                    <div className="ml-3">
                      <div className="text-sm font-medium text-gray-900">
                        {member.name}
                        {isCurrentUser && (
                          <span className="ml-1 text-xs text-gray-500">(you)</span>
                        )}
                      </div>
                      <div className="text-sm text-gray-500">{member.email}</div>
                    </div>
                  </div>
                  <RoleBadge role={member.role} />
                </div>
                <div className="mt-3 flex items-center justify-between text-sm">
                  <span
                    className={`px-2 py-1 text-xs font-medium rounded-full ${
                      member.is_active
                        ? 'bg-green-100 text-green-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}
                  >
                    {member.is_active ? 'Active' : 'Inactive'}
                  </span>
                  {!isCurrentUser && (
                    <button
                      onClick={() => setConfirmRemoveId(member.id)}
                      className="text-red-600 hover:text-red-700"
                    >
                      Remove
                    </button>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      </div>

      {/* Pending Invitations */}
      {pendingInvitations.length > 0 && (
        <div className="card overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 bg-yellow-50">
            <h3 className="text-sm font-medium text-yellow-800">
              Pending Invitations ({pendingInvitations.length})
            </h3>
          </div>
          <div className="divide-y divide-gray-200">
            {pendingInvitations.map((invitation) => (
              <div key={invitation.id} className="px-6 py-4 flex items-center justify-between">
                <div>
                  <div className="text-sm font-medium text-gray-900">{invitation.email}</div>
                  <div className="text-sm text-gray-500">
                    Invited as <RoleBadge role={invitation.role} />
                    <span className="mx-2">-</span>
                    Expires{' '}
                    {invitation.expires_at
                      ? new Date(invitation.expires_at).toLocaleDateString()
                      : 'soon'}
                  </div>
                </div>
                <button
                  onClick={() => onCancelInvitation(invitation.id)}
                  className="text-red-600 hover:text-red-700 text-sm"
                >
                  Cancel
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
